// frontend/packages/business-console/src/pages/InvoiceEditor.jsx
//
// Work item 2.17b — Invoice editor
// (`/business/:slug/invoices/new` and `/invoices/:id/edit`). Spec
// source: docs/design/quotes-invoices-console.md §3.1 + §3.2 + §3.7
// + §3.8.
//
// Mirror of QuoteEditor (2.14) against the /v2/crm/invoices endpoints
// (2.12). Differences:
//   - "INVOICE" label / INV-#### number
//   - Due-date field (invoices live or die by due dates) in place of
//     "Valid until"
//   - Send confirm copy mentions "payment link" not "accept link"
//   - Reuses CustomerPicker + QuoteLineItemsEditor components (the
//     line-item editor is shape-identical between quote and invoice)

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import CustomerPicker from './Quotes/CustomerPicker';
import QuoteLineItemsEditor, { newRow } from './Quotes/QuoteLineItemsEditor';
import {
  createInvoiceDraft,
  getInvoice,
  sendInvoice,
  updateInvoiceDraft,
} from '../services/invoiceService';
import { getCustomer } from '../services/crmService';
import { listQuotes, convertQuoteToInvoice } from '../services/quoteService';
import useOfflineOutbox from '../hooks/useOfflineOutbox';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { newClientUuid } from '../lib/outboxStore';
import OfflineStatusChip from '../components/OfflineStatusChip';

const STATE_COLOR = {
  draft: 'default',
  sent: 'info',
  viewed: 'warning',
  partial: 'warning',
  paid: 'success',
  overdue: 'error',
  void: 'default',
};

function fmtUsd(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format((cents || 0) / 100);
}

function customerName(customer) {
  if (!customer) return 'the customer';
  if (customer.companyName) return customer.companyName;
  const fn = customer.firstName || '';
  const ln = customer.lastName || '';
  return `${fn} ${ln}`.trim() || 'the customer';
}

function isDemoMode(search) {
  const params = new URLSearchParams(search || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

function emptyDraft() {
  return {
    customerId: null,
    title: '',
    lineItems: [newRow(0)],
    notes: '',
    terms: '',
    dueAt: null,
    requestReview: false,
  };
}

export default function InvoiceEditor() {
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const prefillCustomerId = id ? null : searchParams.get('customerId');
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  // Post-payment review requests only send when a destination is set on the
  // business (manual reviewLinkUrl wins, else the Google Place ID). Mirrors
  // resolveReviewUrl in crmReviewRequestService — keep this predicate in sync.
  const reviewDestinationConfigured = Boolean(
    business?.reviewLinkUrl || business?.googlePlaceId
  );
  const isDemo = useMemo(
    () =>
      isDemoMode(typeof window !== 'undefined' ? window.location.search : ''),
    []
  );

  const [form, setForm] = useState(emptyDraft);
  const [customer, setCustomer] = useState(null);
  const [number, setNumber] = useState(null);
  const [state, setState] = useState('draft');
  const [serverId, setServerId] = useState(id || null);

  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [dirty, setDirty] = useState(false);

  // 2.19 offline-outbox plumbing. Mirror of QuoteEditor; see comments there.
  // A FRESH Idempotency-Key is minted per save attempt (in saveDraft), NOT
  // one per session - reusing a single key across edits caused
  // `idempotency_key_reuse_body_mismatch` once the body changed.
  const serverIdRef = useRef(serverId);
  serverIdRef.current = serverId;
  const online = useOnlineStatus();

  const outbox = useOfflineOutbox({
    kind: 'invoice',
    sync: async (item) => {
      const targetId = item.serverId || serverIdRef.current;
      const response = targetId
        ? await updateInvoiceDraft(targetId, item.payload, {
            idempotencyKey: item.clientUuid,
          })
        : await createInvoiceDraft(item.payload, {
            idempotencyKey: item.clientUuid,
          });
      const inv = response?.invoice;
      if (inv) {
        if (!targetId) {
          setServerId(inv._id);
          serverIdRef.current = inv._id;
          navigate(`${basePath}/invoices/${inv._id}/edit`, { replace: true });
        }
        setNumber(inv.number);
        setState(inv.state);
        setForm((prev) => ({
          ...prev,
          lineItems:
            Array.isArray(inv.lineItems) && inv.lineItems.length > 0
              ? inv.lineItems
              : prev.lineItems,
        }));
        setDirty(false);
      }
      return {
        serverId: inv?._id || targetId || null,
        serverNumber: inv?.number || null,
      };
    },
  });

  useEffect(() => {
    if (!id) return;
    if (isDemo) {
      setLoading(false);
      setLoadError(
        new Error(
          'Editing an existing draft is disabled in demo mode. Use the New invoice flow.'
        )
      );
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await getInvoice(id);
        if (cancelled) return;
        const inv = res.invoice;
        if (inv.state !== 'draft') {
          setLoadError(
            new Error(
              `This invoice is in '${inv.state}' state — drafts only can be edited.`
            )
          );
          return;
        }
        setNumber(inv.number);
        setState(inv.state);
        setServerId(inv._id);
        setForm({
          customerId: inv.customerId,
          title: inv.title || '',
          lineItems:
            Array.isArray(inv.lineItems) && inv.lineItems.length > 0
              ? inv.lineItems
              : [newRow(0)],
          notes: inv.notes || '',
          terms: inv.terms || '',
          dueAt: inv.dueAt || null,
          requestReview: Boolean(inv.requestReview),
        });
        // getCustomer returns { customer, activities, ... } — unwrap.
        try {
          const res2 = await getCustomer(inv.customerId);
          if (!cancelled && res2?.customer) setCustomer(res2.customer);
        } catch {
          /* ignore */
        }
      } catch (err) {
        if (!cancelled) setLoadError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isDemo]);

  // Pre-fill Bill-to when arriving from CRM detail via
  // /invoices/new?customerId=<id>. Soft-fails so a stale link doesn't
  // block draft creation.
  useEffect(() => {
    if (!prefillCustomerId || isDemo) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getCustomer(prefillCustomerId);
        const cust = res?.customer;
        if (cancelled || !cust) return;
        setCustomer(cust);
        setForm((prev) => ({ ...prev, customerId: cust._id }));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prefillCustomerId, isDemo]);

  const updateForm = (patch) => {
    setForm((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const updateCustomer = (c) => {
    setCustomer(c);
    setForm((prev) => ({ ...prev, customerId: c?._id || null }));
    setDirty(true);
  };

  // Estimate→invoice (D17 convert): on a NEW invoice, once a customer is
  // chosen, surface their open estimates (sent/viewed/accepted) to convert one
  // instead of re-keying it. Convert reuses the proven /convert path: it
  // pre-fills the invoice, links it, and tags the estimate "converted".
  const [convertibleQuotes, setConvertibleQuotes] = useState([]);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState(null);

  useEffect(() => {
    if (id || isDemo || !customer?._id) {
      setConvertibleQuotes([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        // Convertible estimates = sent / viewed / accepted (the operator can
        // invoice before formal acceptance). The list API takes a single
        // state, so fetch all for the customer and filter client-side.
        const res = await listQuotes({
          customerId: customer._id,
          state: 'all',
          limit: 25,
        });
        const CONVERTIBLE = ['sent', 'viewed', 'accepted'];
        const items = (res?.items || []).filter((q) =>
          CONVERTIBLE.includes(q.state)
        );
        if (!cancelled) setConvertibleQuotes(items);
      } catch {
        if (!cancelled) setConvertibleQuotes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isDemo, customer?._id]);

  const handleStartFromEstimate = async (quoteId) => {
    setConverting(true);
    setConvertError(null);
    try {
      const result = await convertQuoteToInvoice(quoteId);
      const newId = result?.invoice?._id;
      if (newId) {
        navigate(`${basePath}/invoices/${newId}/edit`, { replace: true });
        return;
      }
      setConvertError('Conversion did not return an invoice.');
    } catch (err) {
      setConvertError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not convert the estimate.'
      );
    } finally {
      setConverting(false);
    }
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let taxTotal = 0;
    for (const li of form.lineItems) {
      const qty = Number(li.quantity) || 0;
      const lineSub = qty * (li.unitPrice || 0);
      const lineTax = li.taxable
        ? Math.round((lineSub * (li.taxRateBps || 0)) / 10000)
        : 0;
      subtotal += lineSub;
      taxTotal += lineTax;
    }
    return { subtotal, taxTotal, total: subtotal + taxTotal };
  }, [form.lineItems]);

  const validation = useMemo(() => {
    const errors = [];
    if (!form.customerId) errors.push('Choose a customer.');
    const usable = form.lineItems.filter(
      (li) => li.description.trim() && (Number(li.quantity) || 0) > 0
    );
    if (usable.length === 0)
      errors.push(
        'Add at least one line item with a description and quantity.'
      );
    return { canSave: errors.length === 0, errors };
  }, [form]);

  const buildPayload = () => ({
    customerId: form.customerId,
    title: form.title || undefined,
    lineItems: form.lineItems.map((li, idx) => ({
      id: li.id,
      position: idx,
      description: li.description.trim(),
      quantity: String(li.quantity || '0'),
      unitPrice: Math.max(0, Math.round(li.unitPrice || 0)),
      taxable: Boolean(li.taxable),
      taxRateBps: li.taxable && li.taxRateBps != null ? li.taxRateBps : null,
    })),
    notes: form.notes || undefined,
    terms: form.terms || undefined,
    dueAt: form.dueAt || undefined,
    requestReview: Boolean(form.requestReview),
  });

  const saveDraft = async ({ silent } = {}) => {
    if (isDemo) {
      setToast({
        severity: 'info',
        text: 'Saving is disabled in demo mode. Disable ?demo=1 to test against the live tenant.',
      });
      return null;
    }

    const payload = buildPayload();

    // Fresh idempotency key per save attempt (see QuoteEditor). Reusing one
    // key across edits caused `idempotency_key_reuse_body_mismatch`.
    const idempotencyKey = newClientUuid();

    // Offline path (2.19): enqueue + return. See QuoteEditor for the
    // full rationale. Send is online-only.
    if (!online) {
      await outbox.enqueue({
        clientUuid: idempotencyKey,
        payload,
        route: serverId
          ? `PATCH /v2/crm/invoices/${serverId}`
          : 'POST /v2/crm/invoices',
        serverId: serverId || null,
      });
      setDirty(false);
      if (!silent) {
        setToast({
          severity: 'warning',
          text: "Saved locally. We'll sync as soon as you're back online.",
        });
      }
      return { _id: serverId, number, state, _offline: true };
    }

    setSaving(true);
    setSaveError(null);
    try {
      let response;
      if (serverId) {
        response = await updateInvoiceDraft(serverId, payload, {
          idempotencyKey,
        });
      } else {
        response = await createInvoiceDraft(payload, {
          idempotencyKey,
        });
      }
      const inv = response?.invoice;
      if (inv) {
        if (!serverId) {
          setServerId(inv._id);
          serverIdRef.current = inv._id;
          navigate(`${basePath}/invoices/${inv._id}/edit`, { replace: true });
        }
        setNumber(inv.number);
        setState(inv.state);
        setForm((prev) => ({
          ...prev,
          lineItems:
            Array.isArray(inv.lineItems) && inv.lineItems.length > 0
              ? inv.lineItems
              : prev.lineItems,
        }));
        setDirty(false);
      }
      if (!silent) {
        setToast({
          severity: 'success',
          text: `Draft saved${inv?.number ? ` (${inv.number})` : ''}.`,
        });
      }
      return inv;
    } catch (err) {
      const httpStatus = err?.response?.status;
      const isTransient =
        !httpStatus ||
        httpStatus >= 500 ||
        err?.code === 'ERR_NETWORK' ||
        err?.message === 'Network Error';
      if (isTransient) {
        await outbox.enqueue({
          clientUuid: idempotencyKey,
          payload,
          route: serverId
            ? `PATCH /v2/crm/invoices/${serverId}`
            : 'POST /v2/crm/invoices',
          serverId: serverId || null,
        });
        setDirty(false);
        if (!silent) {
          setToast({
            severity: 'warning',
            text: "Couldn't reach the server. Saved locally; we'll retry on reconnect.",
          });
        }
        return { _id: serverId, number, state, _offline: true };
      }
      setSaveError(
        err?.response?.data?.error || err?.message || "Couldn't save draft."
      );
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSendConfirm = async () => {
    if (isDemo) {
      setSendDialogOpen(false);
      setToast({ severity: 'info', text: 'Sending is disabled in demo mode.' });
      return;
    }
    setSending(true);
    setSaveError(null);
    try {
      let savedId = serverId;
      if (!savedId || dirty) {
        const saved = await saveDraft({ silent: true });
        if (!saved) {
          setSending(false);
          return;
        }
        savedId = saved._id || serverId;
      }
      await sendInvoice(savedId);
      setSendDialogOpen(false);
      setToast({ severity: 'success', text: 'Invoice sent.' });
      navigate(`${basePath}/invoices/${savedId}`);
    } catch (err) {
      setSaveError(
        err?.response?.data?.error || err?.message || "Couldn't send."
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) {
    return (
      <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
        <BackLink to={`${basePath}/invoices`} />
        <Alert severity="error" sx={{ mt: 2 }}>
          {loadError?.response?.data?.error ||
            loadError?.message ||
            "Couldn't load this invoice."}
        </Alert>
      </Box>
    );
  }

  const titleText = serverId ? `Editing ${number || 'draft'}` : 'New invoice';

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        maxWidth: 960,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          // Keep Save / Send reachable without scrolling back up on a long
          // form. Sticky under the app bar (same pattern as the Inbox header).
          position: 'sticky',
          top: { xs: 56, md: 64 },
          zIndex: 2,
          backgroundColor: 'background.default',
          py: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <BackLink
            to={`${basePath}/invoices`}
            confirmIfDirty={dirty}
            navigate={navigate}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            /
          </Typography>
          <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
            {titleText}
          </Typography>
          {state ? (
            <Chip
              size="small"
              label={state}
              color={STATE_COLOR[state] || 'default'}
              variant="outlined"
              sx={{ textTransform: 'capitalize', fontWeight: 600 }}
            />
          ) : null}
          {isDemo ? (
            <Chip
              size="small"
              label="Demo"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          ) : null}
          <OfflineStatusChip
            online={online}
            isFlushing={outbox.isFlushing}
            pendingCount={
              outbox.items.filter(
                (it) => it.status === 'pending' || it.status === 'syncing'
              ).length
            }
            failedCount={
              outbox.items.filter((it) => it.status === 'failed').length
            }
            lastSavedAt={outbox.items[0]?.savedAt || null}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            component={RouterLink}
            to={`${basePath}/invoices`}
            size="small"
            disabled={saving || sending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              saving ? <CircularProgress size={14} /> : <SaveOutlinedIcon />
            }
            onClick={() => saveDraft()}
            disabled={!validation.canSave || saving || sending}
          >
            {saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Tooltip title={!online ? "You're offline. Reconnect to send." : ''}>
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={
                  sending ? (
                    <CircularProgress size={14} />
                  ) : (
                    <SendOutlinedIcon />
                  )
                }
                onClick={() => setSendDialogOpen(true)}
                disabled={!validation.canSave || saving || sending || !online}
              >
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {validation.errors.length > 0 ? (
        <Alert severity="info" variant="outlined">
          {validation.errors.join('  ·  ')}
        </Alert>
      ) : null}
      {saveError ? <Alert severity="error">{saveError}</Alert> : null}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 3, sm: 5 },
          fontFamily:
            "'Liberation Sans', 'Noto Sans', Arial, system-ui, sans-serif",
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                color: 'text.primary',
                letterSpacing: '-0.01em',
              }}
            >
              {business?.name || 'Your business'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              The header (logo, address) renders from your business profile on
              send.
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.18em',
                color: 'text.secondary',
                textTransform: 'uppercase',
              }}
            >
              Invoice
            </Typography>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 700,
                fontFamily: 'monospace',
                color: 'text.primary',
                mt: 0.5,
              }}
            >
              {number || 'INV-####'}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}
            >
              Issued on save
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <CustomerPicker
          value={customer}
          onChange={updateCustomer}
          disabled={saving || sending}
          isDemo={isDemo}
        />

        {convertibleQuotes.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              Start from an estimate?
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
            >
              {customerName(customer)} has{' '}
              {convertibleQuotes.length === 1
                ? 'an open estimate'
                : `${convertibleQuotes.length} open estimates`}
              . Convert one to pre-fill this invoice and link them.
            </Typography>
            {convertError && (
              <Alert
                severity="error"
                sx={{ mb: 1 }}
                onClose={() => setConvertError(null)}
              >
                {convertError}
              </Alert>
            )}
            <Stack spacing={1}>
              {convertibleQuotes.map((q) => (
                <Stack
                  key={q._id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }} noWrap>
                      {q.number || 'Estimate'}
                      {q.title ? ` · ${q.title}` : ''}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      $
                      {((q.total || 0) / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={converting}
                    onClick={() => handleStartFromEstimate(q._id)}
                  >
                    Use this
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mt: 3 }}
        >
          <TextField
            fullWidth
            size="small"
            label="Title (optional)"
            value={form.title}
            onChange={(e) => updateForm({ title: e.target.value })}
            disabled={saving || sending}
          />
          <TextField
            size="small"
            type="date"
            label="Due date"
            value={
              form.dueAt ? new Date(form.dueAt).toISOString().slice(0, 10) : ''
            }
            onChange={(e) =>
              updateForm({
                dueAt: e.target.value
                  ? new Date(e.target.value).toISOString()
                  : null,
              })
            }
            disabled={saving || sending}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180 }}
          />
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            display: 'block',
            mb: 1,
          }}
        >
          Line items
        </Typography>
        <QuoteLineItemsEditor
          lineItems={form.lineItems}
          onChange={(next) => updateForm({ lineItems: next })}
          defaultTaxBps={business?.taxConfig?.defaultTaxRateBps || null}
          disabled={saving || sending}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Stack
            spacing={0.5}
            sx={{
              minWidth: 260,
              '& > *': { display: 'flex', justifyContent: 'space-between' },
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Subtotal
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {fmtUsd(totals.subtotal)}
              </Typography>
            </Box>
            {totals.taxTotal > 0 ? (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Tax
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fmtUsd(totals.taxTotal)}
                </Typography>
              </Box>
            ) : null}
            <Divider />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                Total
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 16,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtUsd(totals.total)}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            label="Notes (visible to customer)"
            value={form.notes}
            onChange={(e) => updateForm({ notes: e.target.value })}
            disabled={saving || sending}
            placeholder="Anything the customer should see — assumptions, follow-up notes, etc."
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            label="Terms"
            value={form.terms}
            onChange={(e) => updateForm({ terms: e.target.value })}
            disabled={saving || sending}
            placeholder="e.g. Net 30. 1.5%/mo on past-due balances."
          />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.requestReview)}
                  onChange={(e) =>
                    updateForm({ requestReview: e.target.checked })
                  }
                  disabled={saving || sending}
                />
              }
              label="Request a review after payment"
            />
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mt: -0.5 }}
            >
              Off by default. When on, the customer gets a one-time review
              request ~24h after they pay.
            </Typography>
            {reviewDestinationConfigured ? (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'success.main', mt: 0.25 }}
              >
                A review destination is set for your business.
              </Typography>
            ) : (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'warning.main', mt: 0.25 }}
              >
                No review destination is configured for your business yet, so
                these requests can’t send — they’re skipped (no broken link)
                until one is added. You can still turn this on now; it starts
                working once a destination is set.
              </Typography>
            )}
          </Box>
        </Stack>
      </Paper>

      <Dialog
        open={sendDialogOpen}
        onClose={() => (sending ? null : setSendDialogOpen(false))}
      >
        <DialogTitle>Send {number || 'this invoice'}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sends the <strong>PDF and a payment link</strong> to{' '}
            <strong>{customerName(customer)}</strong>
            {customer?.email ? (
              <>
                {' '}
                (<code>{customer.email}</code>)
              </>
            ) : null}{' '}
            and locks the invoice. You can void it later but you can&apos;t
            unsend.
          </DialogContentText>
          {saveError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {saveError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendConfirm}
            disabled={sending}
            startIcon={
              sending ? <CircularProgress size={14} /> : <SendOutlinedIcon />
            }
          >
            {sending ? 'Sending…' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity || 'info'}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {toast.text}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}

function BackLink({ to, confirmIfDirty, navigate }) {
  const handleClick = (e) => {
    if (!confirmIfDirty) return;
    const ok = window.confirm(
      'Discard unsaved changes and go back to the list?'
    );
    if (!ok) {
      e.preventDefault();
    } else if (navigate) {
      e.preventDefault();
      navigate(to);
    }
  };
  return (
    <Tooltip title="Back to invoices">
      <IconButton
        size="small"
        component={RouterLink}
        to={to}
        onClick={handleClick}
        sx={{ color: 'text.secondary' }}
        aria-label="Back to invoices"
      >
        <ArrowBackRoundedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}
